import {
  trackCheckoutScreenReached,
  trackPaymentMethodAdded,
} from '@getpopsure/analytics';
import { captureException } from '@sentry/browser';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { fetchAccountInfo } from 'actions/user';
import axios, { AxiosError } from 'axios';
import { ErrorWithAction } from 'components/ErrorWithAction';
import LoadingSpinner from 'components/loadingSpinner';
import TimedLoadSpinner from 'components/timedLoadSpinner';
import routes from 'constants/routes';
import { getDocuments } from 'features/checkoutDocuments/checkoutDocuments.selectors';
import { usePollCheckoutDocuments } from 'features/checkoutDocuments/hooks';
import { getPaymentMethods } from 'features/paymentMethods/paymentMethods.selectors';
import { PaymentOption } from 'features/paymentMethodSelector/paymentMethodSelector.models';
import { prepareForPayment } from 'features/paymentMethodSelector/paymentMethodSelector.thunks';
import {
  isCustomerFacingError,
  isReadyForPayment,
} from 'features/paymentMethodSelector/paymentMethodSelector.utils';
import { PriceInfo } from 'features/paymentScreen/paymentScreen.models';
import {
  getCheckoutInfo,
  getReferralCode,
} from 'features/paymentScreen/paymentScreen.selectors';
import {
  onSuccessfulPayment,
  pay,
  PaymentScreenThunkDispatch,
} from 'features/paymentScreen/paymentScreen.thunks';
import { isValidStartDate } from 'features/paymentScreen/utils/changeStartDate.utils';
import { checkHasCheckoutDocuments } from 'features/paymentScreen/utils/hasCheckoutDocuments.utils';
import { isStripeError } from 'features/paymentScreen/utils/paymentMethod.utils';
import { clearReferrerCode } from 'features/referralEngine/actions';
import { useQueryParamValue } from 'hooks/useQueryParamValue';
import lz from 'lz-string';
import { isValidInsuranceType } from 'models/insurances/types';
import { imageTypeMapping } from 'models/insurances/types/mapping';
import { PaymentMethod } from 'models/paymentMethods';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router';
import { getAccountInfo, getUserId } from 'selectors/user';
import { useSafeTranslation } from 'shared/i18n';
import { isMobileApp } from 'shared/util/isMobileApp';
import { paramsSetUrl } from 'shared/util/paramsSetUrl';

import View from './PaymentScreen.view';
// TODO: detect stale checkouts and show a human-friendly error
//       - when the checkout has been successful in the past, redirect to the policy page
//       - when it expired before being finalized, suggest a way to recover (start the signup from scratch)
//
//       The current checkout screen works around that by displaying a custom message with a link to the
//       start date question
const PaymentScreen = () => {
  const { checkoutId }: { checkoutId: string } = useParams();

  const { t } = useSafeTranslation();
  const dispatch = useDispatch<PaymentScreenThunkDispatch>();
  const history = useHistory();
  const referralCode = useSelector(getReferralCode);
  const paymentOptions = useSelector(getPaymentMethods);
  const checkoutInfo = useSelector(getCheckoutInfo(checkoutId));
  const userId = useSelector(getUserId);
  const redirectStatus = useQueryParamValue('redirect_status');
  const [paymentOption, setPaymentOption] = useState<PaymentOption>({
    type: 'NOT_STARTED',
  });
  const account = useSelector(getAccountInfo);
  const { localeId }: { localeId: string } = useParams();
  const { flagsReady } = useFlagsStatus();
  const isPayPalAvailable = useFlag('app_payment_method_paypal');

  const configurationParam = useQueryParamValue('c');
  const decompressedConfiguration = configurationParam
    ? lz.decompressFromEncodedURIComponent(configurationParam)
    : undefined;
  const configuration = decompressedConfiguration
    ? JSON.parse(decompressedConfiguration)
    : undefined;

  const documents = useSelector(
    getDocuments(checkoutInfo?.metaData?.questionnaireId)
  );
  const checkoutInsuranceType = checkoutInfo?.mainPolicy.policyDetails.type;
  isValidInsuranceType(checkoutInsuranceType);

  const hasCheckoutDocuments = checkHasCheckoutDocuments(
    configuration,
    checkoutInsuranceType
  );

  const { startPollingDocuments, documentsError, documentsLoading } =
    usePollCheckoutDocuments(
      checkoutInsuranceType ?? 'GENERIC',
      checkoutInfo?.metaData?.questionnaireId
    );

  useEffect(() => {
    if (hasCheckoutDocuments) {
      startPollingDocuments();
    }
  }, [hasCheckoutDocuments, startPollingDocuments]);

  if (!checkoutInfo) {
    throw Error(
      'The checkout info object is missing, TODO: implement fetchCheckoutInfo'
    );
  }

  if (!configuration) {
    throw Error('Missing payment screen configuration');
  }

  const {
    totalProratedPrice,
    totalMonthlyPrice,
    mainPolicy: {
      proratedPeriod,
      policyDetails: { type: insuranceType },
    },
  } = checkoutInfo;

  const priceInfo: PriceInfo = {
    totalMonthlyPrice,
    totalProratedPrice,
    proratedPeriod,
  };

  // TODO: Consider moving the icon setup somewhere else; the container should not be concerned with the view
  const icon =
    configuration.icon ??
    imageTypeMapping[insuranceType] ??
    imageTypeMapping.GENERIC;

  const hasPayPal = paymentOptions.find((method) => method.type === 'PAYPAL');

  const stripeClient = useStripe();
  const elements = useElements();

  if (!isMobileApp && (isPayPalAvailable || hasPayPal)) {
    elements?.update({
      paymentMethodTypes: ['sepa_debit', 'card', 'paypal'],
    });
  }

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [isProcessingPayment, setIsProcessingPayment] =
    useState<boolean>(false);
  const [paymentTypeSelected, setPaymentTypeSelected] = useState<
    string | undefined
  >();

  const isLoading =
    !stripeClient ||
    !elements ||
    isProcessingPayment ||
    (hasCheckoutDocuments && documentsLoading);

  const isSubmitButtonEnabled = !isLoading && isReadyForPayment(paymentOption);
  const isBeforePaypalRedirect =
    paymentOption.type === 'READY_FOR_SETUP_WITH_REDIRECT' &&
    paymentTypeSelected === 'paypal';

  useEffect(() => {
    dispatch(fetchAccountInfo());
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (account?.isDelinquent) {
      history.push(routes.policies.delinquency.path);
    }
  }, [account?.isDelinquent, history]);

  useEffect(() => {
    if (flagsReady && userId) {
      trackCheckoutScreenReached({
        variant: 'stripe',
        insurance_type: insuranceType,
        user_id: userId ?? null,
        medium: 'generic_checkout_screen',
      });
    }
    // eslint-disable-next-line
  }, [flagsReady, userId]);

  if (redirectStatus || account?.isDelinquent) {
    return <LoadingSpinner />;
  }

  const showNetworkError = (error: AxiosError) => {
    let message = t(
      'paymentScreen.errors.genericError',
      'We could not process your payment because of an error on our side, please contact support.'
    );

    if (error.response?.status === 402) {
      if (
        typeof error.response.data === 'object' &&
        error.response.data !== null
      ) {
        const data = error.response.data as Record<string, unknown>;
        if ('message' in data && typeof data.message === 'string') {
          message = data.message;
        }
      }
    }
    setErrorMessage(message);
  };

  const showStripeError = (error: StripeError) => {
    setErrorMessage(
      error.message ??
        t(
          'paymentScreen.errors.genericStripeError',
          'There was an issue with your payment method, please contact support.'
        )
    );
  };

  const handleCheckout = async () => {
    setErrorMessage(undefined);
    setIsProcessingPayment(true);

    let paymentMethod: PaymentMethod;

    try {
      if (!isReadyForPayment) {
        throw Error(
          '[Generic checkout] Attempting to pay while the component is not ready for payment'
        );
      }

      if (!stripeClient) {
        throw Error(
          "[Generic checkout] The stripe client hasn't been initialized when the checkout was attempted"
        );
      }
      if (!elements) {
        throw Error(
          '[Generic checkout] The Stripe elements object was not ready in time for payment'
        );
      }

      const basePath = localeId
        ? generatePath(routes.paymentScreenRegionalised.confirmSetup.path, {
            checkoutId,
            localeId,
          })
        : generatePath(routes.paymentScreen.confirmSetup.path, { checkoutId });
      const returnUrl = `${window.location.origin}${basePath}`;

      paymentMethod = await dispatch(
        prepareForPayment({
          paymentOption,
          stripeClient,
          elements,
          returnUrl: paramsSetUrl(returnUrl, [
            { key: 'c', value: configurationParam ?? '' },
          ]),
        })
      );

      trackPaymentMethodAdded({
        variant: 'stripe',
        insurance_type: insuranceType,
        user_id: userId ?? null,
        medium: 'generic_checkout_screen',
        payment_type: paymentMethod.type,
        is_default_payment: paymentMethod.isDefault,
      });

      setPaymentOption({
        type: 'EXISTING_PAYMENT_METHOD',
        paymentMethod,
      });
    } catch (error: unknown) {
      setIsProcessingPayment(false);

      if (axios.isAxiosError(error)) {
        showNetworkError(error);
      } else if (isStripeError(error)) {
        showStripeError(error);

        if (!isCustomerFacingError(error)) {
          // This error should be reported to Sentry very rarely. If this shows up as
          // a false-positive, please update the list of reported error types in isCustomerFacingError,
          // or remove this captureException entirely
          captureException(error);
        }

        return;
      }
      throw error;
    }

    try {
      const { isDuplicatePaymentAttempt } = await dispatch(
        pay({
          checkoutInfo,
          paymentMethod,
          stripeClient,
          region: checkoutInfo.metaData?.regionOfPurchase ?? 'de',
          referralCode,
          configurationQueryParam: configurationParam ?? undefined,
        })
      );

      await dispatch(
        onSuccessfulPayment({
          t,
          history,
          verticalId: checkoutInfo.mainPolicy.policyDetails.type,
          policyId: checkoutInfo.mainPolicy.id,
          isDuplicatePaymentAttempt,
          region: checkoutInfo.metaData?.regionOfPurchase ?? 'de',
          redirectAddDependent: configuration.redirectAddDependent,
          genericQuestionnaireKey: configuration.genericQuestionnaireKey,
        })
      );

      dispatch(clearReferrerCode());
    } catch (error: unknown) {
      setIsProcessingPayment(false);

      if (axios.isAxiosError(error)) {
        showNetworkError(error);
      } else if (isStripeError(error)) {
        showStripeError(error);
        return;
      }

      throw error;
    }
  };

  if (hasCheckoutDocuments && documentsError) {
    return (
      <ErrorWithAction
        title={t('checkout.documents.error.title', 'Something went wrong')}
        description={t(
          'checkout.documents.error.description',
          "Some of the required documents couldn't be generated.\n\nGoing back to the previous page and trying again should fix the issue."
        )}
        cta={{
          title: t('checkout.documentsError.cta', 'Go back'),
          onClick: () => history.goBack(),
        }}
      />
    );
  }

  const onStripePaymentMethodTypeChange = (type: string) => {
    setPaymentTypeSelected(type);
  };

  if (hasCheckoutDocuments && documentsLoading) {
    return (
      <TimedLoadSpinner
        title={t('genericCheckout.loading.title', 'Preparing your policy')}
        description={t(
          'genericCheckout.loading.description',
          "We're gathering your documents and policy details so you can review them before purchasing."
        )}
        delayInMilliseconds={0}
      />
    );
  }

  if (documentsError) {
    return (
      <ErrorWithAction
        title={t(
          'genericCheckout.documents.error.title',
          'Something went wrong'
        )}
        description={t(
          'genericCheckout.documents.error.description',
          "Some of the required documents couldn't be generated.\n\nGoing back to the previous page and trying again should fix the issue."
        )}
        cta={{
          title: t('genericCheckout.documentsError.cta', 'Go back'),
          onClick: () => history.goBack(),
        }}
      />
    );
  }

  return (
    <View
      policyDetailsProps={{
        checkoutInfo,
        configuration: configuration?.policyDetails,
      }}
      priceInfo={priceInfo}
      error={errorMessage}
      isLoading={isLoading}
      isSubmitButtonEnabled={isSubmitButtonEnabled}
      icon={icon}
      handleCheckout={handleCheckout}
      setPaymentOption={setPaymentOption}
      onStripePaymentMethodTypeChange={onStripePaymentMethodTypeChange}
      isBeforePaypalRedirect={isBeforePaypalRedirect}
      documents={documents}
      hasCheckoutDocuments={hasCheckoutDocuments}
      isValidStartDate={isValidStartDate(checkoutInfo)}
      changeStartDatePath={configuration.changeStartDatePath}
    />
  );
};

export default PaymentScreen;
